import {
  Actor,
  Animation,
  AnimationStrategy,
  Canvas,
  Color,
  Engine,
  vec,
  Vector,
} from "excalibur";
import { tileToWorld, worldToTile } from "@/utils/tile";
import { loadedAssets } from "@/loader";
import {
  compareSpriteDescriptions,
  getClothingByPart,
  parseSpriteDescription,
} from "@/utils/character";
import { createTintedMaterial } from "@/utils/materials";
import Location from "@/scenes/location";
import { ChatBubble } from "./chat-bubble";
import FigureThumbnail from "@/utils/figure-thumbnail";
import AssetLoader from "@/utils/asset-loader";
// Define the states for the FSM
export enum FigureState {
  Idle = "idle",
  Walking = "walk",
  Sitting = "sit",
}

// Define an enum for directions
enum Direction {
  Down = "down",
  Left = "left",
  Right = "right",
  Top = "top",
}

// Define a direction mapping enum
enum DirectionMap {
  Down = "down",
  Top = "top",
  Left = "side",
  Right = "side",
}

class FSM {
  private currentState: FigureState;
  private stateHandlers: Map<FigureState, () => void>;

  constructor(initialState: FigureState) {
    this.currentState = initialState;
    this.stateHandlers = new Map();
  }

  public setState(state: FigureState): void {
    if (this.currentState !== state) {
      this.currentState = state;
      if (this.stateHandlers.has(state)) {
        this.stateHandlers.get(state)!();
      }
    }
  }

  public getState(): FigureState {
    return this.currentState;
  }

  public onState(state: FigureState, handler: () => void): void {
    this.stateHandlers.set(state, handler);
  }
}

// Initialize actions and directions
const actions: FigureState[] = [
  FigureState.Idle,
  FigureState.Walking,
  FigureState.Sitting,
];

const directions: Direction[] = [
  Direction.Down,
  Direction.Left,
  Direction.Right,
  Direction.Top,
];

export class Figure extends Actor {
  public x: number;
  public y: number;

  public fsm: FSM;
  private direction = "down";
  private pathToDestination: any = null;
  private hairActor: Actor;
  private headActor: Actor;
  private bodyActor: Actor;
  public isSitting = false;
  private tilemapPosition: any;

  look: any;
  username: string;
  legsActor: Actor;
  hitArea: Actor;
  thumbnailData: { resource: any; x: number; y: number; tint: string }[];
  torsoActor: Actor;
  direcionMap: any;
  myNineSlice: any;
  previousChatBubble: ChatBubble;
  dustActor: Actor;
  dustAnimation: Animation;
  clientId: string;
  thumbnail: any;
  assetLoader: any;

  constructor({
    username,
    clientId,
    playerPosition,
    cameraPosition,
    direction,
    look,
  }: {
    username: string;
    clientId: string;
    playerPosition: Vector;
    cameraPosition: Vector;
    direction: string;
    look: string;
  }) {
    super({
      z: 0,
    });

    this.x = playerPosition.x;
    this.y = playerPosition.y;
    this.username = username;
    this.clientId = clientId;
    this.pos = cameraPosition.add(tileToWorld(playerPosition));
    this.direction = direction || "down";

    this.tilemapPosition = cameraPosition;
    this.look = look;
    this.assetLoader = new AssetLoader();
    this.direcionMap = this.getDirectionMap();
  }

  public async onInitialize(_engine: Engine) {
    this.dustActor = new Actor({});
    this.setFigureStates();

    await this.loadLook();
    await this.initAnimation();
    await this.setThumbnailData();

    this.flipSprite();
    this.setupPointerEvents();
    this.tintSprite();
    this.setZIndex(this.x + this.y);
  }

  public async loadLook() {
    const hair = getClothingByPart(this.look, "hr");
    const legs = getClothingByPart(this.look, "lg");
    const torso = getClothingByPart(this.look, "ts");

    const resources = [
      { path: "./public/figures/body/", slug: "head", assetId: "head" },
      { path: "./public/figures/body/", slug: "body", assetId: "body" },
      { path: "./public/figures/hair/", slug: hair.type, assetId: hair.type },
      { path: "./public/figures/legs/", slug: legs.type, assetId: legs.type },
      {
        path: "./public/figures/torso/",
        slug: torso.type,
        assetId: torso.type,
      },
    ];

    try {
      // Load all assets
      await this.assetLoader.loadMultipleAsepriteResources(resources);
    } catch (error) {
      console.error("Error loading assets:", error);
    }
  }

  public tintSprite(look?: string) {
    if (!this.scene) return;
    const extractedLook = this.extractLook(this.look || look);

    const skinTint = createTintedMaterial(
      this.scene.engine,
      extractedLook.skinColorRGB
    );

    const legsTint = createTintedMaterial(
      this.scene.engine,
      extractedLook.legsColorRGB
    );

    const torsoTint = createTintedMaterial(
      this.scene.engine,
      extractedLook.torsoColorRGB
    );

    const hairTint = createTintedMaterial(
      this.scene.engine,
      extractedLook.hairColorRGB
    );

    this.headActor.graphics.material = skinTint;
    this.bodyActor.graphics.material = skinTint;
    this.legsActor.graphics.material = legsTint;
    this.torsoActor.graphics.material = torsoTint;
    this.hairActor.graphics.material = hairTint;
  }

  private extractLook(look: string) {
    const { skinColor } = parseSpriteDescription(look);
    const skinColorRGB = Color.fromHex(skinColor);
    skinColorRGB.a = 0.5;

    const hair = getClothingByPart(look, "hr");
    const hairColorRGB = Color.fromHex(hair.color);
    hairColorRGB.a = 0.7;

    const legs = getClothingByPart(look, "lg");
    const legsColorRGB = Color.fromHex(legs.color);
    legsColorRGB.a = 0.7;

    const torso = getClothingByPart(look, "ts");
    const torsoColorRGB = Color.fromHex(torso.color);
    torsoColorRGB.a = 0.7;

    return {
      skinColorRGB,
      skinColorHex: skinColor, // Return original hex value
      hairColorRGB,
      hairColorHex: hair.color, // Return original hex value
      legsColorRGB,
      legsColorHex: legs.color, // Return original hex value
      torsoColorRGB,
      torsoColorHex: torso.color, // Return original hex value
      hairType: hair.type,
      legsType: legs.type,
      torsoType: torso.type,
    };
  }

  private async setThumbnailData() {
    const extractedLook = this.extractLook(this.look);

    const action = "down";
    const animation = "idle_" + action;
    const thumb = new FigureThumbnail();
    const thumbnail = thumb.createMultipleThumbnails({
      head: {
        x: 17,
        y: 0,
        animationName: action,
        color: extractedLook.skinColorRGB.toString(),
      },
      [extractedLook.hairType]: {
        x: 17,
        y: 0,
        animationName: action,
        color: extractedLook.hairColorRGB.toString(),
      },
      body: {
        x: 0,
        y: -65,
        animationName: animation,
        color: extractedLook.skinColorRGB.toString(),
      },
      [extractedLook.legsType]: {
        x: 0,
        y: -65,
        animationName: animation,
        color: extractedLook.legsColorRGB.toString(),
      },
      [extractedLook.torsoType]: {
        x: 0,
        y: -65,
        animationName: animation,
        color: extractedLook.torsoColorRGB.toString(),
      },
    });

    this.thumbnail = thumbnail;
  }

  public async setLook(look: string) {
    const locationScene = this.scene as Location;
    const lookResources = [{ look }];

    await locationScene.loadLookResources(lookResources);

    const diffrences = compareSpriteDescriptions(this.look, look);
    const updated = Object.keys(diffrences.updated);

    // If a torso is found, update its animation and graphics
    const torso = getClothingByPart(look, "ts");
    if (torso && updated.includes("ts"))
      this.updateActorAnimationActions("torsoActor", torso.type);

    const hair = getClothingByPart(look, "hr");

    if (hair && updated?.includes("hr")) {
      this.updateActorAnimationDirections("hairActor", hair.type);
    }

    const legs = getClothingByPart(look, "lg");
    if (legs && updated?.includes("lg"))
      this.updateActorAnimationActions("legsActor", legs.type);

    this.tintSprite(look);

    this.showDustCloud();
  }

  private setupPointerEvents(): void {
    const scene = this.scene as Location;

    this.hitArea.on("pointerdown", async (e) => {
      scene.furniViewer?.closeView();

      await scene.userViewer?.modify({
        clientId: this.clientId,
        username: this.username,
        thumbnail: this.thumbnail,
      });

      scene.userViewer?.openView();

      e.cancel();
    });
  }

  public setFigureStates() {
    this.fsm = new FSM(FigureState.Idle);

    // Define FSM state behaviors
    this.fsm.onState(FigureState.Idle, () =>
      this.setAnimation(FigureState.Idle)
    );

    this.fsm.onState(FigureState.Walking, () =>
      this.setAnimation(FigureState.Walking)
    );

    this.fsm.onState(FigureState.Sitting, () =>
      this.setAnimation(FigureState.Sitting)
    );
  }

  private async initAnimation(): Promise<void> {
    // Now, create actors and set animations
    this.bodyActor = this.createActor(vec(0, 0), 0.5, 1, 0);
    this.updateActorAnimationActions("bodyActor", "body");
    this.addChild(this.bodyActor);

    // Get body actor's top boundary for alignment
    const bodyActorY = this.bodyActor.graphics.localBounds.top;

    // Create head actor aligned with body and set animations
    this.headActor = this.createActor(vec(0, bodyActorY), 0.5, 1, 0);
    this.updateActorAnimationDirections("headActor", "head");
    this.addChild(this.headActor);

    // Create hair actor if hair data exists
    const hair = getClothingByPart(this.look, "hr");

    if (hair?.type) {
      this.hairActor = this.createActor(vec(0, 0), 0.5, 1, 1);
      this.updateActorAnimationDirections("hairActor", hair.type);
      this.headActor.addChild(this.hairActor);
    }

    // Create torso actor if torso data exists
    const torso = getClothingByPart(this.look, "ts");
    if (torso?.type) {
      this.torsoActor = this.createActor(vec(0, 0), 0.5, 1, 1);
      this.updateActorAnimationActions("torsoActor", torso.type);
      this.bodyActor.addChild(this.torsoActor);
    }

    // Create legs actor if legs data exists
    const legs = getClothingByPart(this.look, "lg");
    if (legs?.type) {
      this.legsActor = this.createActor(vec(0, 0), 0.5, 1, 1);
      this.updateActorAnimationActions("legsActor", legs.type);
      this.bodyActor.addChild(this.legsActor);
    }

    this.hitArea = new Actor({
      width: 30,
      height: 100,
      z: 2,
      anchor: vec(0.5, 1),
    });

    this.addChild(this.hitArea);
  }

  showDustCloud() {
    if (!this.dustAnimation) return;
    this.dustActor.graphics.use(this.dustAnimation);
    this.dustAnimation.reset();
  }

  // Helper function to create actor
  private createActor(
    position: Vector,
    anchorX: number,
    anchorY: number,
    zIndex: number = 1,
    offset?: Vector
  ): Actor {
    return new Actor({
      pos: position,
      anchor: vec(anchorX, anchorY),
      z: zIndex,
      offset,
    });
  }

  // Helper function to get the direction map
  private getDirectionMap(): { [key in Direction]: DirectionMap } {
    return {
      [Direction.Down]: DirectionMap.Down,
      [Direction.Top]: DirectionMap.Top,
      [Direction.Left]: DirectionMap.Left,
      [Direction.Right]: DirectionMap.Right,
    };
  }

  private updateActorAnimationActions(actor: string, key: string): void {
    const actorInstance = this[actor]; // Get actor dynamically
    const asset = this.assetLoader.getAssetById(key);
    if (!asset) return;

    // Dynamically retrieve actions and directions
    actions.forEach((action) => {
      directions.forEach((direction) => {
        const animationKey = `${action}_${this.direcionMap[direction]}`;
        const animation = asset.resource.getAnimation(animationKey);

        // Check if the actor has a valid graphics property before adding
        if (actorInstance && actorInstance.graphics) {
          actorInstance.graphics.add(`${action}-${direction}`, animation);
        }
      });
    });

    // Default to a base animation state for the actor
    if (actorInstance && actorInstance.graphics) {
      actorInstance.graphics.use(`${FigureState.Idle}-${this.direction}`);
    }
  }

  private updateActorAnimationDirections(actor: string, key: string): void {
    const actorInstance = this[actor]; // Get actor dynamically
    const asset = this.assetLoader.getAssetById(key);
    if (!asset) return;

    // Dynamically retrieve actions and directions
    directions.forEach((direction) => {
      const animationKey = `${this.direcionMap[direction]}`;
      const animation = asset.resource.getAnimation(animationKey);

      // Check if the actor has a valid graphics property before adding
      if (actorInstance && actorInstance.graphics) {
        actorInstance.graphics.add(`${direction}`, animation);
      }
    });

    // Default to a base animation state for the actor
    if (actorInstance && actorInstance.graphics) {
      actorInstance.graphics.use(`${this.direction}`);
    }
  }

  addChat(text: string, isTyping: boolean) {
    this.previousChatBubble?.remove();

    const chatBubble = new ChatBubble();

    const bodyCenterX =
      this.bodyActor.graphics.localBounds.left +
      this.bodyActor.graphics.localBounds.width / 2;

    const offsetY =
      this.bodyActor.graphics.localBounds.top +
      this.headActor.graphics.localBounds.top -
      40;

    chatBubble.displayText({
      text,
      isTyping,
      scene: this,
      bodyCenterX,
      offsetY,
    });

    this.previousChatBubble = chatBubble;
  }

  public update(engine: Engine, delta: number): void {
    super.update(engine, delta);
    this.handleWalking(delta);
  }

  public movePlayer(path: any, action: string) {
    this.pathToDestination = path;
    if (action === "sit") this.isSitting = true;
  }

  public getDirectionToTarget(currentPosition, targetTile) {
    // Get the world position of the target tile
    const targetTileCenter = this.tilemapPosition.add(tileToWorld(targetTile));
    const dx = targetTileCenter.x - currentPosition.x;
    const dy = targetTileCenter.y - currentPosition.y;

    // Determine the direction based on the differences in x and y
    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal movement
      if (dx > 0) {
        return Direction.Right;
      } else {
        return Direction.Left;
      }
    } else {
      // Vertical movement
      if (dy > 0) {
        return Direction.Down;
      } else {
        return Direction.Top;
      }
    }
  }

  private handleWalking(delta: number): void {
    if (this.pathToDestination?.length > 0) {
      const currentTile = this.pathToDestination[0];
      const targetTileCenter = this.tilemapPosition.add(
        tileToWorld(currentTile)
      );

      const distanceToTarget = this.pos.distance(targetTileCenter);
      const furniture = (this.scene as any).furnitureManager.getByPosition(
        currentTile.x,
        currentTile.y
      )[0];

      if (distanceToTarget <= 5) {
        if (furniture) {
          this.fsm.setState(FigureState.Sitting);
        } else {
          this.fsm.setState(FigureState.Walking);
        }

        this.pathToDestination.shift();
      }

      if (!furniture) {
        this.setAnimation(FigureState.Walking);
      }

      const direction = targetTileCenter.sub(this.pos).normalize();
      const movement = direction.scale((200 * delta) / 1000);

      this.pos = this.pos.add(movement);
      this.setZIndex(currentTile.y);

      this.setDirection(currentTile);

      this.x = currentTile.x;
      this.y = currentTile.y;
    } else {
      if (this.fsm.getState() !== FigureState.Sitting) {
        this.fsm.setState(FigureState.Idle);
      }
    }
  }

  private setZIndex(value: number) {
    this.hairActor.z = value + 1;
    this.headActor.z = value;
    this.bodyActor.z = value - 1;
    this.dustActor.z = value + 1;
  }

  private setAnimation(action: FigureState) {
    // Define the graphics configuration for each actor
    const graphicsConfig = {
      torsoActor: `${action}-${this.direction}`,
      bodyActor: `${action}-${this.direction}`,
      legsActor: `${action}-${this.direction}`,
      headActor: this.direction,
      hairActor: this.direction,
    };

    // Apply the graphics settings to each actor
    Object.entries(graphicsConfig).forEach(([key, value]) => {
      this[key].graphics.use(value);
    });

    // Set offset for "sit" or default to 0
    const offsetY = action === "sit" ? 19 : 0;
    this.children.forEach((child: Actor) => {
      child.offset = vec(0, offsetY);
    });
  }

  private setDirection(tile: { x: number; y: number }) {
    const currentTile = this.getTilePosition(this.pos);
    const scene = this.scene as Location;
    const furniture = scene.furnitureManager.getByPosition(tile.x, tile.y)[0];

    if (furniture) {
      const directionMap = {
        1: "down",
        2: "left",
        4: "right",
        default: "top",
      };

      this.direction =
        directionMap[furniture.direction] || directionMap.default;
      if (furniture.direction === 1 && furniture.canSit) {
        // this.setZIndex(currentTile.x + currentTile.y + 5);
      }
    } else {
      const dx = tile.x - currentTile.x;
      const dy = tile.y - currentTile.y;

      if (dx > 0) this.direction = "right";
      else if (dx < 0) this.direction = "left";
      else if (dy > 0) this.direction = "down";
      else if (dy < 0) this.direction = "top";
    }

    this.flipSprite();
  }

  private flipSprite(): void {
    const flip = this.direction === "right"; // Determine if flip is needed
    [
      this.bodyActor,
      this.headActor,
      this.hairActor,
      this.torsoActor,
      this.legsActor,
    ].forEach((actor) => {
      if (actor && actor.graphics) {
        actor.graphics.flipHorizontal = flip;
      }
    });
  }

  private getTilePosition(position?: Vector) {
    const playerPosition = position || this.pos;
    return worldToTile(playerPosition.sub(this.tilemapPosition));
  }
}
